import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useHealth, useLogout, useMe } from '../../hooks/queries'
import { gemerkterOrt, startziel, type Startziel } from '../../lib/letzterChat'
import { useOnline, useOutboxAnzahl } from '../../hooks/useOutbox'
import { BackIcon } from '../../components/Icons'
import { DiensteCard } from './DiensteCard'
import { LimitsCard } from './LimitsCard'
import { PushCard } from './PushCard'

export function SettingsPage() {
  const me = useMe()
  const logout = useLogout()
  const navigate = useNavigate()

  return (
    <>
      <header className="bar">
        <Link className="icon-button" to="/" aria-label="Zurück">
          <BackIcon />
        </Link>
        <h2 className="bar__title bar__grow">Einstellungen</h2>
      </header>

      <div className="scroll-panel">
        <div className="card" style={{ margin: '0 auto 16px', boxShadow: 'none' }}>
          <h1 style={{ fontSize: 17 }}>{me.data?.display_name}</h1>
          <p className="card__lead">{me.data?.email}</p>

          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: -6 }}>
            {me.data?.may_manage_people
              ? 'Du darfst Reisen anlegen und Personen freischalten.'
              : me.data?.may_create_trips
                ? 'Du darfst eigene Reisen anlegen.'
                : 'Du liest mit und antwortest.'}
          </p>

          {me.data?.may_manage_people && (
            <Link
              className="button button--outline"
              to="/settings/people"
              style={{ marginBottom: 10 }}
            >
              Personen verwalten
            </Link>
          )}

          <button
            className="button button--outline"
            type="button"
            disabled={logout.isPending}
            onClick={() =>
              logout.mutate(undefined, {
                onSuccess: () => navigate('/login', { replace: true }),
              })
            }
          >
            Abmelden
          </button>
        </div>

        <PushCard />

        <LimitsCard />

        <DiensteCard />

        {isIos() && !isStandalone() && (
          <div className="card" style={{ margin: '0 auto', boxShadow: 'none' }}>
            <h1 style={{ fontSize: 17 }}>Auf dem iPhone installieren</h1>
            {/* Ohne diesen Schritt gibt es auf iOS keine Benachrichtigungen --
                Apple gibt die Push-Schnittstelle nur Web-Apps auf dem
                Home-Bildschirm, nicht Seiten im Safari-Tab. */}
            <p className="card__lead" style={{ marginBottom: 0 }}>
              Tippe in Safari unten auf <strong>Teilen</strong> und dann auf{' '}
              <strong>Zum Home-Bildschirm</strong>. Nur dann kann dich die App
              später über neue Nachrichten benachrichtigen.
            </p>
          </div>
        )}

        {/* Ganz unten, und das mit Absicht: die beiden Karten sind
            Nachschlagewerk für den Fehlerfall, nicht Bedienung. Wer hier
            etwas einstellen will, soll nicht erst an Messwerten vorbei. */}
        <Netzlage />

        <Tastaturlage />

        <Stand />
      </div>
    </>
  )
}

/**
 * Welcher Stand hier läuft -- und welcher auf dem Server.
 *
 * Zwei Angaben statt einer, weil genau ihr Auseinanderfallen der Fehler war,
 * den sonst niemand sieht: ein Browser, der eine alte Fassung aus dem
 * Zwischenspeicher fährt, sieht vollkommen gesund aus.
 *
 * Aus demselben Grund steht hier auch, **wo die Anwendung beim nächsten Mal
 * aufmacht**. Aus dem Betrieb: am iPad kam der verlassene Chat, am iPhone
 * immer irgendein Chat — und ob dort nichts gemerkt war oder etwas Falsches,
 * ließ sich von außen nicht sehen. Eine Zeile beendet das Raten; sie steht
 * neben den Ständen, weil beides zur selben Frage gehört, wenn zwei Geräte
 * sich verschieden verhalten.
 */
function Stand() {
  const health = useHealth(true)

  return (
    <p
      style={{
        margin: '18px auto 0',
        maxWidth: 420,
        fontSize: 12,
        textAlign: 'center',
        color: 'var(--text-muted)',
      }}
    >
      Travel Broadcast {health.data?.version ?? '—'} · Oberfläche {__BUILD__} ·
      Server {health.data?.build ?? '—'}
      <br />
      Anzeige: {anzeigeArt()} · {herkunft()} · beim Öffnen gelesen:{' '}
      {nenne(startziel())} · jetzt gemerkt: {nenne(gemerkterOrt())}
    </p>
  )
}

/**
 * Womit die Seite geladen wurde — und ob der Browser das als sicher ansieht.
 *
 * Aus dem Betrieb: in der Web-App meldet iOS „Die Verbindung zu dieser Website
 * ist nicht sicher" mit einem leeren Zertifikat, in Safari dagegen das gültige.
 * Von außen sind dafür zwei ganz verschiedene Ursachen möglich — die App läuft
 * tatsächlich über `http`, weil das Symbol einmal von einer unverschlüsselten
 * Adresse angelegt wurde, oder jenes Blatt sagt in einer Web-App schlicht
 * nichts Sinnvolles. Der Unterschied entscheidet, ob etwas zu reparieren ist,
 * und die Seite selbst weiß ihn.
 */
function herkunft(): string {
  return `${location.protocol}${window.isSecureContext ? '' : ' (unsicher)'}`
}

/** „Chat /t/7", „Übersicht" oder „nichts" -- in einer Zeile lesbar. */
function nenne(ziel: Startziel): string {
  if (ziel === null) return 'nichts'
  return ziel.art === 'liste' ? 'Übersicht' : `Chat ${ziel.pfad}`
}

/**
 * Was das Gerät über seine Verbindung denkt -- und was noch aussteht.
 *
 * Diese Zeile beendet ein Ratespiel. Aus dem Betrieb: „Flugmodus an, aber kein
 * Hinweis" — und in Wahrheit war das WLAN weiter an, denn der Flugmodus
 * schaltet es auf dem iPhone nicht zwingend ab. Das Flugzeugsymbol sagt eben
 * nichts darüber, ob etwas durchgeht.
 *
 * Deshalb stehen hier **drei Angaben nebeneinander**: was der Browser meldet
 * (`navigator.onLine`), was zuletzt tatsächlich passiert ist (`netzStand()`)
 * und wie viele Nachrichten warten. Fallen die ersten beiden auseinander,
 * hängt das Gerät an einem Netz, durch das nichts hindurchgeht.
 */
function Netzlage() {
  const online = useOnline()
  const wartend = useOutboxAnzahl()
  const [browser, setBrowser] = useState(() => navigator.onLine)

  useEffect(() => {
    const pruefen = () => setBrowser(navigator.onLine)
    window.addEventListener('online', pruefen)
    window.addEventListener('offline', pruefen)
    return () => {
      window.removeEventListener('online', pruefen)
      window.removeEventListener('offline', pruefen)
    }
  }, [])

  return (
    <div className="card" style={{ margin: '0 auto 16px', boxShadow: 'none' }}>
      <h1 style={{ fontSize: 17 }}>Verbindung</h1>
      <p className="card__lead" style={{ marginBottom: 8 }}>
        {online
          ? 'Es geht gerade etwas durch.'
          : 'Zurzeit kommt nichts durch — was du schreibst, wartet im Gerät.'}
      </p>

      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-muted)' }}>
        <li>Zuletzt tatsächlich erreicht: {online ? 'ja' : 'nein'}</li>
        <li>Der Browser meldet: {browser ? 'online' : 'offline'}</li>
        <li>
          Wartende Nachrichten: <strong>{wartend}</strong>
        </li>
      </ul>

      {browser && !online && (
        <p className="notice" style={{ marginTop: 10 }}>
          Das Gerät hängt an einem Netz, durch das nichts hindurchgeht — etwa
          einem WLAN ohne Verbindung nach draußen. Das kommt auch im Flugmodus
          vor, wenn das WLAN dabei angeschaltet bleibt.
        </p>
      )}

      {/* Eine unverschlüsselte Verbindung fällt sonst nicht auf, richtet aber
          still Schaden an: ohne sie gibt es keine Benachrichtigungen, und was
          hier steht, liest jeder im selben Netz mit. Auf dem Telefon entsteht
          das meist so, dass das Symbol einmal von einer `http`-Adresse aus
          angelegt wurde -- die Web-App startet dann für immer dort. */}
      {!window.isSecureContext && (
        <p className="notice" style={{ marginTop: 10 }}>
          Diese Seite läuft <strong>unverschlüsselt</strong> ({location.protocol}
          ). Benachrichtigungen sind damit nicht möglich, und mitlesen kann
          jeder im selben Netz. Ruf sie über <code>https://</code> auf — und
          wenn du sie auf dem Home-Bildschirm hast, lösche das Symbol und lege
          es von der sicheren Adresse aus neu an.
        </p>
      )}
    </div>
  )
}

/**
 * Was das Gerät tut, wenn die Tastatur aufgeht.
 *
 * Dieselbe Absicht wie bei *Verbindung* darüber: eine Frage beenden, die von
 * außen nicht zu beantworten ist. Ob die Eingabezeile unter der Tastatur
 * liegt, hängt an vier Zahlen, und keine davon ist zu sehen — der Prüfstand
 * hier hat keine iOS-Tastatur, und ein Bildschirmfoto allein sagt nicht, ob
 * die Fläche verschoben oder der Abstand falsch gemessen ist.
 *
 * Deshalb ein Feld zum Hineintippen und die Zahlen darunter, die sich dabei
 * bewegen. Fehlt der Abstand, während die Tastatur offen ist, misst der
 * Browser anders als angenommen; ist der Versatz groß, schiebt das Gerät die
 * Fläche.
 */
function Tastaturlage() {
  const [werte, setWerte] = useState(lies)

  useEffect(() => {
    const sicht = window.visualViewport
    const auffrischen = () => setWerte(lies())

    sicht?.addEventListener('resize', auffrischen)
    sicht?.addEventListener('scroll', auffrischen)
    window.addEventListener('resize', auffrischen)
    return () => {
      sicht?.removeEventListener('resize', auffrischen)
      sicht?.removeEventListener('scroll', auffrischen)
      window.removeEventListener('resize', auffrischen)
    }
  }, [])

  // Wo der Browser die Tastatur selbst wegrechnet, gibt es hier nichts zu
  // sehen und nichts zu erklären.
  if (!window.visualViewport) return null

  return (
    <div className="card" style={{ margin: '0 auto 16px', boxShadow: 'none' }}>
      <h1 style={{ fontSize: 17 }}>Tastatur</h1>
      <p className="card__lead" style={{ marginBottom: 8 }}>
        Tippe ins Feld. Bleibt es über der Tastatur sichtbar, sitzt es richtig.
      </p>

      <input
        className="field__input"
        type="text"
        placeholder="Hier tippen"
        aria-label="Testfeld für die Tastatur"
        style={{ marginBottom: 10 }}
      />

      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-muted)' }}>
        <li>
          Freigehaltener Abstand: <strong>{werte.abstand}</strong>
        </li>
        <li>Fensterhöhe: {werte.fenster}</li>
        <li>Sichtbare Höhe: {werte.sicht}</li>
        <li>Versatz der Fläche: {werte.versatz}</li>
        <li>Seitenversatz: {werte.seite}</li>
      </ul>
    </div>
  )
}

function lies() {
  const sicht = window.visualViewport
  return {
    fenster: Math.round(window.innerHeight),
    sicht: sicht ? Math.round(sicht.height) : 0,
    versatz: sicht ? Math.round(sicht.offsetTop) : 0,
    seite: Math.round(window.scrollY),
    abstand:
      getComputedStyle(document.documentElement).getPropertyValue('--tastatur-hoehe').trim() ||
      '0px',
  }
}

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
}

/**
 * Läuft das hier als Web-App vom Home-Bildschirm?
 *
 * Auf iOS zählt `navigator.standalone` und sonst nichts. Der Anzeigemodus
 * allein reicht nicht: In-App-Browser können `display-mode: standalone`
 * melden, weil aus Sicht der Seite keine Leisten da sind — und dann bliebe
 * der Hinweis zum Installieren ausgerechnet dem verborgen, der ihn braucht.
 */
function isStandalone(): boolean {
  const alsApp = (navigator as { standalone?: boolean }).standalone
  if (alsApp !== undefined) return alsApp
  return window.matchMedia('(display-mode: standalone)').matches
}

/**
 * Web-App oder Browser — und wenn Browser, ob eines mit Leisten.
 *
 * Aus dem Betrieb, und es hat eine Runde gekostet: ein Bildschirmfoto der
 * Standzeile kam aus einem In-App-Browser, während am Gerät sonst die Web-App
 * benutzt wird. Beides sieht innen gleich aus, hat unter iOS aber **je einen
 * eigenen Speicher** — was in dem einen gemerkt wurde, gibt es im anderen
 * nicht. Wer das nicht weiß, hält es für einen Fehler der Anwendung.
 *
 * `navigator.standalone` ist dabei die verlässlichere Auskunft: manche
 * In-App-Browser melden `display-mode: standalone` mit, weil aus Sicht der
 * Seite keine Leisten da sind. Fallen die beiden auseinander, steht genau das
 * da — geraten wird nicht.
 */
function anzeigeArt(): string {
  const alsApp = (navigator as { standalone?: boolean }).standalone
  const ohneLeisten = window.matchMedia('(display-mode: standalone)').matches

  if (alsApp === true) return 'App'
  if (alsApp === false) return ohneLeisten ? 'Browser (ohne Leisten)' : 'Browser'
  // Außerhalb von iOS gibt es das Feld nicht; dort genügt der Anzeigemodus.
  return ohneLeisten ? 'App' : 'Browser'
}
